import { useEffect, useState } from 'react';
import { NumberTile } from './HomeEditorial';

// Rotating carousel for the home-page "League Stats" section. Pages through a
// flat list of stat tiles, auto-advancing every `intervalMs` (default 30s).
// Pauses on hover so readers can finish a tile. Dots below the grid show page
// position and let the reader jump pages manually.

const DEFAULT_INTERVAL_MS = 30000;
const DEFAULT_PAGE_SIZE = 6;

export function StatCarousel({
  tiles,
  pageSize = DEFAULT_PAGE_SIZE,
  intervalMs = DEFAULT_INTERVAL_MS,
}) {
  const pageCount = Math.max(1, Math.ceil(tiles.length / pageSize));
  const [pageIndex, setPageIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // `fadeKey` re-mounts the grid on every page change so the transition runs cleanly.
  const [fadeKey, setFadeKey] = useState(0);

  useEffect(() => {
    if (paused || pageCount <= 1) return undefined;
    const id = window.setInterval(() => {
      setPageIndex((prev) => (prev + 1) % pageCount);
      setFadeKey((k) => k + 1);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [paused, pageCount, intervalMs]);

  function jumpToPage(idx) {
    if (idx === pageIndex) return;
    setPageIndex(idx);
    setFadeKey((k) => k + 1);
  }

  const start = pageIndex * pageSize;
  const visibleTiles = tiles.slice(start, start + pageSize);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        key={fadeKey}
        className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[220px] animate-stat-fade-in"
      >
        {visibleTiles.map((tile, i) => (
          <NumberTile key={`${pageIndex}-${i}`} tile={tile} />
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        <div className="font-display tracking-[0.28em] text-[10px] uppercase text-ink-tertiary">
          {paused ? 'Paused — hover to read' : `Auto-cycling every ${Math.round(intervalMs / 1000)}s`}
        </div>
        <div className="flex items-center gap-2" role="tablist" aria-label="Stat carousel pages">
          {Array.from({ length: pageCount }, (_, i) => (
            <button
              type="button"
              key={i}
              role="tab"
              aria-selected={i === pageIndex}
              aria-label={`Show stat page ${i + 1} of ${pageCount}`}
              onClick={() => jumpToPage(i)}
              className={`h-2 transition-all ${
                i === pageIndex
                  ? 'w-8 bg-accent-gold'
                  : 'w-2 bg-ink-tertiary/30 hover:bg-ink-tertiary/60'
              }`}
            />
          ))}
        </div>
        <div className="font-display tracking-[0.28em] text-[10px] uppercase text-ink-tertiary tabular-nums">
          {String(pageIndex + 1).padStart(2, '0')} / {String(pageCount).padStart(2, '0')}
        </div>
      </div>
    </div>
  );
}

export default StatCarousel;
